// @ts-nocheck
/* eslint react/prop-types: 0 */
import React, { useEffect, useRef, useState } from "react";
// @ts-ignore
import { useAllTranslationsData, useAllManualsData, useAllSitesData } from "@staticQueries";
import WebViewNavBar from "./WebViewNavBar";
import {
  parkPreviewSession,
  restorePreviewSession,
  isPreviewParked,
  PREVIEW_PARAM_KEYS,
} from "../utils/previewParams";
import { savePreferredSiteId } from "../utils/sitePreference";
import digiGuideLogo from "../images/DigiGuide_logo_3_cropped.png";
import dciLogo from "../images/DCI.png";
import DigiGuideLaunchLink from "./DigiGuideLaunchLink";

const PhoneIcon = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block', flexShrink: 0 }}>
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

const DciLogoBox = ({ size = 13 }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: size, height: size,
    background: '#0B83FF', borderRadius: '3px',
    flexShrink: 0,
  }}>
    <img src={dciLogo} alt="DCI" style={{ width: size * 0.7, height: size * 0.7, objectFit: 'contain', display: 'block' }} />
  </span>
);

const font = "'Open Sans', system-ui, sans-serif";

// ── Slug helper ───────────────────────────────────────────────────────────────

function slugify(text) {
  return (text || '')
    .replace(/<[^>]+>/g, '')       // strip HTML tags
    .replace(/&[a-z]+;/gi, '')     // strip HTML entities
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

// ── Shared DOM helper — exported so preview template can call it too ───────────

/**
 * Stamps canonical IDs onto chapter/topic title elements.
 * Format: {slug(title)}-cid-{chapterId}  (chapters)
 *         {slug(title)}-cid-{chapterId}-tid-{topicId}  (topics)
 * Unique even when the same topic appears in multiple chapters.
 * Must be called AFTER ensureHeadingIds so it overrides slug-based IDs.
 */
export function addEntryIdAnchors(container, manual) {
  const chapterEls = [...container.querySelectorAll('.chapter')];
  (manual.chapters || []).forEach((chapter, i) => {
    const chapterEl = chapterEls[i];
    if (!chapterEl) return;
    const chapterHeading = chapterEl.querySelector('.title-chapter');
    if (chapterHeading) {
      chapterHeading.id = `${slugify(chapter.title || chapter.userTitle)}-cid-${chapter.id}`;
    }
    const topicEls = [...chapterEl.querySelectorAll('.topic')];
    (chapter.topics || []).forEach((topic, j) => {
      const topicEl = topicEls[j];
      if (!topicEl) return;
      const topicHeading = topicEl.querySelector('.title-topic');
      if (topicHeading) {
        topicHeading.id = `${slugify(topic.title || topic.userTitle)}-cid-${chapter.id}-tid-${topic.id}`;
      }
    });
  });
}

// ── Fallback element lookup by cid/tid suffix ─────────────────────────────────

/**
 * Find a heading element by its cid (and optionally tid) numeric IDs,
 * regardless of the title-slug prefix.  Used as fallback when the title has
 * changed and the exact ID no longer matches.
 */
function findHeadingByCidTid(hashOrId) {
  const id = hashOrId.startsWith('#') ? hashOrId.slice(1) : hashOrId;
  // New format: *-cid-{n} or *-cid-{n}-tid-{m}
  const cidMatch = id.match(/(?:^|-)cid-(\d+)(?:-tid-(\d+))?$/);
  // Old format: chapterId-{n} or chapterId-{n}-topicId-{m}
  const oldTopicMatch = !cidMatch && id.match(/^chapterId-(\d+)-topicId-(\d+)$/);
  const oldChapterMatch = !cidMatch && !oldTopicMatch && id.match(/^chapterId-(\d+)$/);

  let cid, tid;
  if (cidMatch) {
    [, cid, tid] = cidMatch;
  } else if (oldTopicMatch) {
    [, cid, tid] = oldTopicMatch;
  } else if (oldChapterMatch) {
    [, cid] = oldChapterMatch;
  } else {
    return null;
  }

  const suffix = tid ? `cid-${cid}-tid-${tid}` : `cid-${cid}`;
  return [...document.querySelectorAll('[id]')].find(e =>
    e.id.endsWith(`-${suffix}`) || e.id === suffix
  ) || null;
}

// ── URL parsing ───────────────────────────────────────────────────────────────

/**
 * Parse the current URL hash and build all link-card URLs.
 * Supports new format (#slug-cid-{n}-tid-{m}) and old format (#chapterId-{n}-topicId-{m}).
 */
function parseLinkCardFromUrl(manual, { isPreview = false } = {}) {
  const hash = window.location.hash || '';

  // New format: any-slug-cid-{n}-tid-{m} / any-slug-cid-{n}
  // Old format: #chapterId-{n}-topicId-{m} / #chapterId-{n}
  const topicMatch = hash.match(/-cid-(\d+)-tid-(\d+)$/) ||
                     hash.match(/^#chapterId-(\d+)-topicId-(\d+)$/);
  const chapterMatch = !topicMatch && (
    hash.match(/-cid-(\d+)$/) ||
    hash.match(/^#chapterId-(\d+)$/)
  );

  let appUrl, chapterId = null, topicId = null;

  if (topicMatch) {
    chapterId = topicMatch[1];
    topicId = topicMatch[2];
    appUrl = `dci-digiguide:///link/TopicArticle?id=${topicId}&chapterId=${chapterId}&manualId=${manual?.id}`;
  } else if (chapterMatch) {
    chapterId = chapterMatch[1];
    appUrl = `dci-digiguide:///link/Chapter?id=${chapterId}&manualId=${manual?.id}`;
  } else {
    appUrl = `dci-digiguide:///link/Manual?id=${manual?.id}`;
  }

  const chapter = chapterId ? manual?.chapters?.find(c => String(c.id) === chapterId) : null;
  const topic = topicId ? chapter?.topics?.find(t => String(t.id) === topicId) : null;

  const pdfBase = process.env.GATSBY_PDF_BASE_URL;
  const pdfUrl = pdfBase
    ? `${pdfBase}/DCI_DigiGuide_Manual_${manual?.id}_Site_${manual?.siteId}.pdf`
    : null;

  // Universal /link/ page URL — null when no manual ID available
  const linkUrl = manual?.id
    ? topicId
      ? `/link/TopicArticle?id=${topicId}&chapterId=${chapterId}&manualId=${manual.id}`
      : chapterId
        ? `/link/Chapter?id=${chapterId}&manualId=${manual.id}`
        : `/link/Manual?id=${manual.id}`
    : null;

  // Clean URL — strip preview params and 'link' param
  const cleanParams = new URLSearchParams(window.location.search);
  cleanParams.delete('link');
  PREVIEW_PARAM_KEYS.forEach(k => cleanParams.delete(k));
  const qs = cleanParams.toString();
  const currentUrl = window.location.pathname + (qs ? `?${qs}` : '') + hash;

  const staticWebUrl = manual?.id && manual?.siteId
    ? `/web/manuals/${manual.id}/site/${manual.siteId}${hash}`
    : currentUrl;

  return {
    appUrl,
    pdfUrl,
    linkUrl,
    webUrl: isPreview ? staticWebUrl : currentUrl,
    previewUrl: isPreview ? currentUrl : null,
    chapterId,
    topicId,
    chapterTitle: chapter?.title || null,
    topicTitle: topic?.title || null,
  };
}

// ── Build prod-view toggle URL ────────────────────────────────────────────────

function buildProdToggleUrl(enableProd) {
  const url = new URL(window.location.href);
  // Strip sensitive preview params — they live in sessionStorage, not links
  ['token', 'x-craft-live-preview', 'x-craft-preview', 'api-url'].forEach(k => url.searchParams.delete(k));
  if (enableProd) {
    url.searchParams.set('production', '1');
  } else {
    url.searchParams.delete('production');
  }
  return url.toString();
}

// ── Nav height helper ─────────────────────────────────────────────────────────

/** Read the always-visible nav bar height for scroll offset calculations. */
function getNavOffset() {
  return (parseInt(document.documentElement.style.getPropertyValue('--link-card-nav-height')) || 44) + 20;
}

/** Scroll to an element found by ID or cid/tid fallback, updating the hash. */
function scrollToHeading(hashOrId, opts = {}) {
  const id = (hashOrId.startsWith('#') ? hashOrId.slice(1) : hashOrId);
  let target = document.getElementById(id);
  if (!target) target = findHeadingByCidTid(id);
  if (!target) return;
  const offset = opts.offset !== undefined ? opts.offset : getNavOffset();
  const top = target.getBoundingClientRect().top + window.scrollY - offset;
  const smooth = opts.smooth !== false; // default true
  window.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
  const newHash = `#${target.id}`;
  if (smooth) {
    window.history.pushState(null, null, newHash);
  } else {
    window.history.replaceState(null, null, newHash);
  }
  window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL: window.location.href, newURL: window.location.origin + window.location.pathname + newHash }));
}

// ── Link card sections hook ───────────────────────────────────────────────────
// Called unconditionally at top of component; guards internally when linkCard is null.

function useLinkCardSections({ manual, linkCard, isPreview, isProductionView, hasPreviewToken, isLinkMode }) {
  const allTranslations = useAllTranslationsData();
  const allManuals = useAllManualsData();
  const allSites = useAllSitesData();
  const siteId = String(manual?.siteId || '1');
  const tMap = allTranslations[siteId] || {};
  const t = (key, fallback) => (tMap[key] !== undefined ? tMap[key] : fallback);

  // Build sites for the language selector — only sites where this manual actually exists
  const allSitesForSelector = React.useMemo(() => {
    const manualId = String(manual?.id || "");
    // Set of siteIds where this specific manual exists
    const liveSiteIds = new Set(
      allManuals
        .filter(m => String(m.id) === manualId)
        .map(m => String(m.siteId))
    );

    if (allSites && allSites.length > 0) {
      return [...allSites]
        .filter(s => s.enabled !== false && liveSiteIds.has(String(s.craftSiteId)))
        .map(s => ({
          siteId: String(s.craftSiteId),
          locale: s.locale || s.language || String(s.craftSiteId),
          name: s.name || s.locale || String(s.craftSiteId),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    // Fallback: derive from allManuals filtered to this manual
    const seen = new Set();
    const sites = [];
    allManuals.forEach(m => {
      const sid = String(m.siteId);
      if (String(m.id) === manualId && !seen.has(sid)) {
        seen.add(sid);
        sites.push({
          siteId: sid,
          locale: m.language || m.siteHandle || sid,
          name: m.language || m.siteHandle || sid,
        });
      }
    });
    return sites.sort((a, b) => a.name.localeCompare(b.name));
  }, [allSites, allManuals, manual?.id]);

  const noData = { LogoSection: null, BreadcrumbRow: null, t: (k, fb) => fb, siteId: "1", allSitesForSelector: [], buildLangUrl: null, appUrl: null, family: null, variant: null, chapterId: null, topicId: null, manualTitle: '', logoUrl: null };
  if (!linkCard) return noData;

  const chapterLabel = t('screen_chapter', 'Chapter');
  const articleLabel = t('screen_article', 'Article');

  const { chapterId, topicId, chapterTitle, topicTitle, appUrl, pdfUrl, linkUrl, webUrl, previewUrl } = linkCard;
  const manualTitle = manual?.userTitle || manual?.title || '';

  const launchVerb = t('link_click_launch', 'Open');
  const openContext = topicTitle
    ? { noun: t('screen_article', 'Article'), title: topicTitle }
    : chapterTitle
      ? { noun: t('screen_chapter', 'Chapter'), title: chapterTitle }
      : { noun: t('print_user_manual', 'User Manual'), title: manualTitle };

  // Language selector — navigate directly to the same manual in the selected site language,
  // preserving the current hash (chapter/topic position).
  const buildLangUrl = (newSiteId) => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const search = typeof window !== 'undefined' ? window.location.search : '';
    return `/web/manuals/${manual?.id}/site/${newSiteId}${search}${hash}`;
  };

  const handleAnchorClick = (e, href) => {
    if (!href || !href.startsWith('#')) return;
    e.preventDefault();
    scrollToHeading(href, { smooth: true });
  };

  const breadcrumbs = [
    // Manual title — not a link; clicking it would navigate to the same page and cause a remount
    { label: manualTitle, href: null, dimmed: true },
    chapterTitle ? { label: `${chapterLabel}: ${chapterTitle}`, href: `#cid-${chapterId}` } : null,
    topicTitle ? { label: `${articleLabel}: ${topicTitle}`, href: `#cid-${chapterId}-tid-${topicId}` } : null,
  ].filter(Boolean);

  const logoUrl = manual?.logo?.url;
  const family = manual?.productFamily;
  const variant = manual?.variant;

  // Compute preview / parked state before building JSX
  const isParked = typeof window !== 'undefined' ? isPreviewParked() : false;
  const isLivePreviewActive = isPreview && !isParked;
  const livePreviewPdfUrl = (isLivePreviewActive && typeof window !== 'undefined')
    ? (() => {
        const url = new URL(window.location.href);
        url.searchParams.set('web', '0');
        url.searchParams.delete('production');
        return url.toString();
      })()
    : null;

  /* ── Logo section ───────────────────────────────────────────────────────── */
  const LogoSection = (
    <div
      className="wvh-logo-section"
      style={{
        background: '#fff',
        borderBottom: '1px solid #eef0f5',
      }}
    >
      <div style={{
        maxWidth: '816px', margin: '0 auto',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '1rem 1.5rem',
        minHeight: '96px', gap: '0.1rem',
        fontFamily: font,
      }}>
        <img
          src={digiGuideLogo}
          alt="DigiGuide"
          style={{ maxWidth: '320px', maxHeight: '60px', width: '100%', objectFit: 'contain' }}
        />
      </div>
    </div>
  );

  /* ── Breadcrumb row ─────────────────────────────────────────────────────── */
  const BreadcrumbRow = (
    <div style={{ padding: '0.3rem 0 0' }}>
      {/* DigiGuide logo above the card */}
      <div style={{ padding: '0 0.75rem 0.12rem', display: 'flex', alignItems: 'center' }}>
        <img
          src={digiGuideLogo}
          alt="DigiGuide"
          style={{ height: '13px', width: 'auto', objectFit: 'contain', opacity: 0.55 }}
        />
      </div>

      {/* Combined app-launch + in-page breadcrumb card */}
      <div
        className="wvh-breadcrumb-inner"
        style={{
          background: '#fff',
          borderRadius: '8px',
          border: '1px solid rgba(122, 128, 145, 0.15)',
          boxShadow: '3px 3px 8px rgba(122, 128, 145, 0.15)',
          padding: '0.3rem 0.75rem',
          fontSize: '0.68rem',
          fontFamily: font,
          minHeight: 'calc(2 * 1.5em + 0.6rem)',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.2rem 0.25rem',
        }}
      >
        {/* Blue launch pill — shows full app navigation destination */}
        <a
          href={appUrl || 'dci-digiguide:///'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.15rem 0.25rem',
            background: '#0066cc',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: '5px',
            padding: '0.2rem 0.45rem',
            fontWeight: 600,
            flexShrink: 0,
            lineHeight: 1.4,
          }}
        >
          {/* DCI logo — white tint on blue background */}
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 13, height: 13, background: 'rgba(255,255,255,0.22)', borderRadius: '2px', flexShrink: 0,
          }}>
            <img src={dciLogo} alt="" style={{ width: 9, height: 9, objectFit: 'contain', display: 'block', filter: 'brightness(0) invert(1)' }} />
          </span>
          <span>DigiGuide</span>
          <span style={{ opacity: 0.55 }}>›</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
            {t('link_click_launch', 'Launch')} {t('screen_manual', 'Manual')}
            <PhoneIcon size={11} />
          </span>
          {chapterTitle && (
            <>
              <span style={{ opacity: 0.55 }}>›</span>
              <span style={{ whiteSpace: 'nowrap', fontWeight: 400 }}>{chapterLabel}: {chapterTitle}</span>
            </>
          )}
          {topicTitle && (
            <>
              <span style={{ opacity: 0.55 }}>›</span>
              <span style={{ whiteSpace: 'nowrap', fontWeight: 400 }}>{articleLabel}: {topicTitle}</span>
            </>
          )}
        </a>

        {/* Chevron pointing toward the breadcrumb context on the right */}
        <span style={{ color: '#bbb', flexShrink: 0, fontSize: '1.1em' }}>›</span>

        {/* Product context: family, logo, variant */}
        {family && <span style={{ opacity: 0.5, whiteSpace: 'nowrap', fontSize: '0.9em' }}>{family}</span>}
        {logoUrl && (
          <img src={logoUrl} alt="" loading="lazy"
            style={{ maxHeight: '12px', maxWidth: '48px', objectFit: 'contain', opacity: 0.85, flexShrink: 0, display: 'block' }} />
        )}
        {variant && <span style={{ opacity: 0.6, whiteSpace: 'nowrap', fontSize: '0.9em', fontWeight: 600, color: 'rgb(11,131,255)' }}>{variant}</span>}
        {manualTitle && !family && !logoUrl && !variant && (
          <span style={{ color: 'inherit', opacity: 0.6, whiteSpace: 'nowrap' }}>{manualTitle}</span>
        )}

        {/* Chapter in-page anchor */}
        {chapterTitle && (
          <>
            <span style={{ color: '#bbb', flexShrink: 0 }}>›</span>
            <a
              href={`#cid-${chapterId}`}
              onClick={e => handleAnchorClick(e, `#cid-${chapterId}`)}
              style={{ color: '#555', textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
              {chapterLabel}: {chapterTitle}
            </a>
          </>
        )}

        {/* Topic in-page anchor */}
        {topicTitle && (
          <>
            <span style={{ color: '#bbb', flexShrink: 0 }}>›</span>
            <a
              href={`#cid-${chapterId}-tid-${topicId}`}
              onClick={e => handleAnchorClick(e, `#cid-${chapterId}-tid-${topicId}`)}
              style={{ color: '#555', textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
              {articleLabel}: {topicTitle}
            </a>
          </>
        )}
      </div>
    </div>
  );

  return { LogoSection, BreadcrumbRow, t, siteId, allSitesForSelector, buildLangUrl, appUrl, family, variant, chapterId, topicId, manualTitle, logoUrl };
}

// ── WebViewHeader component ───────────────────────────────────────────────────

/**
 * Fixed top header for web view and preview pages.
 *
 * The logo section scrolls away with the page (top = -min(scrollY, logoHeight));
 * the nav row (breadcrumb + action) stays pinned at the top once the logo is gone.
 * No CSS animation — the collapse is driven entirely by scroll position.
 *
 * Props:
 *   manual            — resolved manual object
 *   containerRef      — ref to the scrollable content container (for active-section tracking)
 *   isPreview         — show preview badge + importexport + exit-preview button
 *   isProductionView  — affects badge label (PROD VIEW vs LIVE PREVIEW)
 */
const WebViewHeader = ({
  manual,
  containerRef,
  isPreview = false,
  isProductionView = false,
  hasPreviewToken = false,
  chapterGroups = [],
}) => {
  const isLinkMode = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('link') === 'digiguide';

  const [linkCard, setLinkCard] = useState(null);
  const linkCardRef = useRef(null);
  const logoSectionRef = useRef(null);
  const navSectionRef = useRef(null);

  // useLinkCardSections must be called unconditionally (contains hooks)
  const { LogoSection, BreadcrumbRow, t, siteId, allSitesForSelector, buildLangUrl, appUrl, family, variant, chapterId: linkChapterId, topicId: linkTopicId, manualTitle: linkManualTitle, logoUrl: linkLogoUrl } = useLinkCardSections({
    manual,
    linkCard,
    isPreview,
    isProductionView,
    hasPreviewToken,
    isLinkMode,
  });

  const isParked = typeof window !== 'undefined' ? isPreviewParked() : false;

  const handleToggleLivePreview = () => {
    if (isParked) {
      restorePreviewSession();
      window.location.reload();
    } else {
      parkPreviewSession();
      const url = new URL(window.location.href);
      PREVIEW_PARAM_KEYS.forEach(k => url.searchParams.delete(k));
      window.location.href = url.toString();
    }
  };

  // Initialise link card from URL and update on hash changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => setLinkCard(parseLinkCardFromUrl(manual, { isPreview }));
    update();
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll: slide logo away with scroll, active-section tracking
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Set initial top without putting it in React inline style (avoids re-render reset)
    if (linkCardRef.current) linkCardRef.current.style.top = '0px';

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          // Scroll the full header up; logo disappears, nav bar pins at top
          if (linkCardRef.current && logoSectionRef.current) {
            const logoH = logoSectionRef.current.offsetHeight;
            const scrolledLogo = Math.min(window.scrollY, logoH);
            linkCardRef.current.style.top = `-${scrolledLogo}px`;
            // Track visible bottom so hamburger flyout anchors to it
            const visibleBottom = linkCardRef.current.offsetHeight - scrolledLogo;
            document.documentElement.style.setProperty('--link-card-bottom', `${visibleBottom}px`);
          }

          // Active-section tracking — update breadcrumb as user scrolls
          if (containerRef && containerRef.current) {
            const navH = parseInt(document.documentElement.style.getPropertyValue('--link-card-nav-height')) || 44;
            const headerOffset = navH + 20;
            const allHeadings = [...containerRef.current.querySelectorAll('.title-chapter[id], .title-topic[id]')];
            let activeHeading = null;
            for (const h of allHeadings) {
              if (h.getBoundingClientRect().top <= headerOffset) {
                activeHeading = h;
              } else {
                break;
              }
            }
            if (activeHeading) {
              const newHash = `#${activeHeading.id}`;
              if (newHash !== window.location.hash) {
                const oldURL = window.location.href;
                window.history.replaceState(null, null, newHash);
                const newURL = window.location.href;
                window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL, newURL }));
                setLinkCard(parseLinkCardFromUrl(manual, { isPreview }));
              }
            } else if (window.scrollY < 50 && window.location.hash) {
              // Scrolled back to top — clear hash so breadcrumb shows just the manual
              const oldURL = window.location.href;
              window.history.replaceState(null, null, window.location.pathname + window.location.search);
              const newURL = window.location.href;
              window.dispatchEvent(new HashChangeEvent('hashchange', { oldURL, newURL }));
              setLinkCard(parseLinkCardFromUrl(manual, { isPreview }));
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Smooth scroll for all hash anchor links — capture phase so we fire before
  // Gatsby's global click handler (which would otherwise treat hash links as
  // client-side navigations and re-render the page). Covers both in-content
  // links and the hamburger menu (which lives outside containerRef on body).
  useEffect(() => {
    const handleClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute('href');
      e.preventDefault(); // stops browser nav + Gatsby's link catcher (which checks defaultPrevented)
      scrollToHeading(href, { smooth: true });
    };
    document.addEventListener('click', handleClick, { capture: true });
    return () => document.removeEventListener('click', handleClick, { capture: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Measure header + nav heights — update CSS vars (drives padding-top via CSS, no React re-render)
  useEffect(() => {
    if (!linkCard || !linkCardRef.current) {
      document.documentElement.style.removeProperty('--link-card-height');
      document.documentElement.style.removeProperty('--link-card-nav-height');
      return;
    }
    const totalEl = linkCardRef.current;
    const navEl = navSectionRef.current;
    const update = () => {
      const totalH = totalEl.offsetHeight;
      document.documentElement.style.setProperty('--link-card-height', `${totalH}px`);
      if (navEl) {
        document.documentElement.style.setProperty('--link-card-nav-height', `${navEl.offsetHeight}px`);
      }
      const logoH = logoSectionRef.current ? logoSectionRef.current.offsetHeight : 0;
      const scrolledLogo = Math.min(window.scrollY || 0, logoH);
      document.documentElement.style.setProperty('--link-card-bottom', `${totalH - scrolledLogo}px`);
    };
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(totalEl);
      if (navEl) ro.observe(navEl);
      return () => ro.disconnect();
    }
  }, [linkCard]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!linkCard) return null;

  return (
    <div
      ref={linkCardRef}
      className="web-view-header-chrome"
      style={{
        position: 'fixed',
        // top is set via DOM in scroll handler, not React inline style
        left: 0, right: 0, zIndex: 500,
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
        pointerEvents: 'auto',
      }}
    >
      {/* Logo row — full width, white background, scrolls away with page */}
      <div ref={logoSectionRef}>
        {LogoSection}
      </div>
      {/* Nav rows — breadcrumb card + action buttons, all on grey background */}
      <div ref={navSectionRef} style={{ background: '#f4f6f9' }}>
        <WebViewNavBar
          manual={manual}
          linkCard={linkCard}
          isPreview={isPreview}
          isProductionView={isProductionView}
          isParked={isParked}
          isLinkMode={isLinkMode}
          chapterGroups={chapterGroups}
          t={t}
          siteId={siteId}
          allSitesForSelector={allSitesForSelector}
          onLangChange={(newSiteId) => { savePreferredSiteId(newSiteId); window.location.href = buildLangUrl(newSiteId); }}
          buildLangUrl={buildLangUrl}
          buildProdToggleUrl={buildProdToggleUrl}
          onToggleLivePreview={handleToggleLivePreview}
          breadcrumbContent={BreadcrumbRow}
        />
      </div>
    </div>
  );
};

export default WebViewHeader;
